/**
 * Admin — pantalla operativa de administración.
 *
 * Tres bloques:
 *  1) Freno maestro global — congela toda la moderación CM con confirmación.
 *  2) Clientes — listar + crear (alta del registro cm_clients).
 *  3) Cuentas — conectar una cuenta IG/FB/WhatsApp nueva a un cliente.
 *
 * Solo accesible para rol admin. El backend ya tiene requireRole('admin')
 * en cada endpoint — la UI se entrega al usuario operador "tal cual" y los
 * botones devuelven 403 si no tiene permiso.
 */
import { useState } from "react";
import { AlertTriangle, KeyRound, Lock, Plus, Power } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  useAdminClients,
  useCreateClient,
  useCreateCmAccount,
  useMasterKillSwitch,
  useSetMasterKillSwitch,
  useSetClientAnthropicKey,
  type AdminClient,
  type ClientType,
  type MasterKillLevel,
} from "@/hooks/use-admin";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { ClientSlug, Platform } from "@/types";

const MASTER_KILL_LEVELS: Array<{
  value: MasterKillLevel;
  label: string;
  description: string;
}> = [
  { value: "none", label: "Ninguno", description: "Operación normal" },
  {
    value: "bot_only",
    label: "Pausar bot global",
    description: "Ningún cliente recibe respuestas automáticas — los humanos siguen",
  },
  {
    value: "bot_and_escalations",
    label: "Bot + escalaciones",
    description: "Sin procesamiento nuevo, sin notificaciones de escalación",
  },
  {
    value: "full",
    label: "Apagado total",
    description: "Toda la moderación congelada hasta levantar el freno",
  },
];

const CLIENT_TYPE_OPTIONS: Array<{ value: ClientType; label: string }> = [
  { value: "consumer", label: "Consumo masivo" },
  { value: "pharma", label: "Pharma" },
  { value: "b2b", label: "B2B" },
  { value: "internal", label: "Interno" },
];

export function AdminPage() {
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="font-sans text-sm text-text-muted">Cargando…</div>
      </div>
    );
  }

  // Page-level guard — el sidebar ya esconde el link a no-admins, pero un
  // operador que navegue por URL directa /admin igual veía el formulario y
  // pegaba 403 silencioso al apretar botones. Mostramos lock state explícito.
  if (user && user.role !== "admin") {
    return (
      <div className="min-h-full bg-background overflow-y-auto">
        <div className="max-w-2xl mx-auto px-7 py-16 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-surface-2 border border-default items-center justify-center mb-4">
            <Lock size={26} strokeWidth={1.5} className="text-text-muted" />
          </div>
          <h1 className="font-sans text-2xl font-semibold text-text-primary">
            Acceso restringido
          </h1>
          <p className="font-sans text-sm text-text-muted mt-2">
            Esta sección es solo para administradores. Si necesitás levantar el
            freno maestro o configurar cuentas, pedíselo a un socio (Marcos, Julián
            o Sebastián).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background overflow-y-auto">
      <div className="max-w-4xl mx-auto px-7 py-8 space-y-8">
        <header>
          <h1 className="font-sans text-3xl font-semibold tracking-tight text-text-primary">
            Admin
          </h1>
          <p className="font-sans text-sm text-text-muted mt-1">
            Operación administrativa de la plataforma. Solo rol admin.
          </p>
        </header>

        <MasterKillSection />
        <TestNotificationsSection />
        <ClientsSection />
        <AccountsSection />
      </div>
    </div>
  );
}

function TestNotificationsSection() {
  const [pending, setPending] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  async function fire(severity: "critical" | "high" | "medium" | "low") {
    setPending(true);
    setLastResult(null);
    try {
      const res = await fetch("/api/admin/trigger-test-alert", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ severity, clientSlug: "champe-agency" }),
      });
      const data = (await res.json()) as { ok?: boolean; message?: string };
      if (data.ok) {
        setLastResult(
          `Alerta ${severity.toUpperCase()} disparada. Revisá mail (mcanton@ + jrodriguez@) y Discord #cm-alertas en los próximos 30s.`,
        );
      } else {
        setLastResult(`Error: ${data.message ?? "respuesta sin ok"}`);
      }
    } catch (err) {
      setLastResult(`Error de red: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="card-surface p-5">
      <h2 className="font-sans text-md font-semibold text-text-primary">
        Test de notificaciones
      </h2>
      <p className="font-sans text-sm text-text-muted mt-1">
        Dispara una alerta sintética para validar que el dispatcher
        (mail + Discord) está funcionando. Llega a los recipients configurados
        para el cliente seleccionado (default: Champe Agency).
      </p>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button
          type="button"
          onClick={() => fire("critical")}
          disabled={pending}
          className={cn(
            "inline-flex items-center gap-2 h-9 px-3 rounded-lg",
            "bg-error-bg border border-error-border text-error-text font-sans text-sm font-medium",
            "hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          {pending ? "Disparando…" : "🔴 Disparar CRÍTICA"}
        </button>
        <button
          type="button"
          onClick={() => fire("high")}
          disabled={pending}
          className={cn(
            "inline-flex items-center gap-2 h-9 px-3 rounded-lg",
            "bg-warning-bg border border-warning-border text-warning-text font-sans text-sm font-medium",
            "hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          {pending ? "Disparando…" : "🟡 Disparar ALTA"}
        </button>
        <button
          type="button"
          onClick={() => fire("medium")}
          disabled={pending}
          className={cn(
            "inline-flex items-center gap-2 h-9 px-3 rounded-lg",
            "bg-transparent border border-default text-text-primary font-sans text-sm font-medium",
            "hover:bg-surface-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          {pending ? "Disparando…" : "🔵 Disparar MEDIA"}
        </button>
      </div>

      {lastResult && (
        <div
          className={cn(
            "mt-3 rounded-md px-3 py-2 text-sm font-sans",
            lastResult.startsWith("Error")
              ? "bg-error-bg border border-error-border text-error-text"
              : "bg-success-bg border border-success-border text-success-text",
          )}
        >
          {lastResult}
        </div>
      )}

      <p className="font-sans text-2xs text-text-muted mt-3">
        Solo CRÍTICA y ALTA llegan a Discord. MEDIA y BAJA solo a mail.
      </p>
    </section>
  );
}

function MasterKillSection() {
  const { data, isLoading } = useMasterKillSwitch();
  const setKill = useSetMasterKillSwitch();
  const [selected, setSelected] = useState<MasterKillLevel>("none");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);

  const currentLevel = data?.level ?? "none";
  const active = currentLevel !== "none";
  const requiresReason = selected !== "none";
  const changed = selected !== currentLevel;

  function handleApply() {
    if (selected === "full" && !confirming) {
      setConfirming(true);
      return;
    }
    setKill.mutate(
      { level: selected, reason: reason || "(sin razón)" },
      {
        onSuccess: () => {
          setConfirming(false);
          setReason("");
        },
      },
    );
  }

  return (
    <section
      aria-labelledby="kill-heading"
      className={cn(
        "card-surface p-5",
        active && "border-l-[3px]",
      )}
      style={active ? { borderLeftColor: "var(--error)" } : undefined}
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Power size={16} strokeWidth={1.75} className="text-text-muted" />
          <h2 id="kill-heading" className="font-sans text-md font-semibold text-text-primary">
            Freno maestro global
          </h2>
        </div>
        {active && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-2xs font-sans font-semibold bg-error-bg text-error-text border border-error-border">
            <AlertTriangle size={11} strokeWidth={2} />
            ACTIVO · {currentLevel}
          </span>
        )}
      </div>
      <p className="font-sans text-sm text-text-muted mb-4">
        Apaga la moderación CM de TODOS los clientes a la vez. Usar solo en emergencia.
      </p>

      {isLoading ? (
        <p className="text-sm text-text-muted">Cargando estado…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {MASTER_KILL_LEVELS.map((opt) => {
              const active = selected === opt.value;
              const isCurrent = currentLevel === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelected(opt.value)}
                  className={cn(
                    "text-left p-3 rounded-lg border transition-colors",
                    active
                      ? "border-magenta bg-state-human-bg"
                      : "border-default bg-surface-1 hover:bg-surface-2",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-sans text-sm font-medium text-text-primary">
                      {opt.label}
                    </span>
                    {isCurrent && (
                      <span className="font-mono text-2xs uppercase tracking-wider text-text-muted">
                        actual
                      </span>
                    )}
                  </div>
                  <p className="font-sans text-xs text-text-muted mt-0.5">
                    {opt.description}
                  </p>
                </button>
              );
            })}
          </div>

          {requiresReason && changed && (
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Razón del freno maestro (queda en bitácora y se envía como alerta crítica)"
              className="w-full h-9 px-3 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none mb-3"
            />
          )}

          {changed && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleApply}
                disabled={setKill.isPending || (requiresReason && !reason)}
                className={cn(
                  "h-9 px-3.5 rounded-lg text-sm font-medium font-sans inline-flex items-center gap-2 transition-colors",
                  selected === "full"
                    ? "bg-error-bg border border-error-border text-error-text hover:opacity-90"
                    : "bg-magenta text-white hover:bg-magenta-hover",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                {confirming
                  ? "Confirmar apagado total"
                  : setKill.isPending
                    ? "Aplicando…"
                    : "Aplicar freno maestro"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelected(currentLevel);
                  setReason("");
                  setConfirming(false);
                }}
                className="h-9 px-3 rounded-lg text-sm font-sans text-text-muted hover:text-text-primary transition-colors"
              >
                Cancelar
              </button>
              {confirming && (
                <span className="font-sans text-xs text-error-text">
                  Apagado TOTAL global. Hacé click otra vez para confirmar.
                </span>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ClientsSection() {
  const { data: clients, isLoading } = useAdminClients();
  const [showForm, setShowForm] = useState(false);

  return (
    <section aria-labelledby="clients-heading" className="card-surface p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 id="clients-heading" className="font-sans text-md font-semibold text-text-primary">
          Clientes con moderación contratada
        </h2>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary font-sans text-xs font-medium transition-colors"
          >
            <Plus size={12} strokeWidth={2} />
            Dar de alta
          </button>
        )}
      </div>

      {showForm && (
        <CreateClientForm onClose={() => setShowForm(false)} />
      )}

      {isLoading ? (
        <p className="font-sans text-xs text-text-muted">Cargando clientes…</p>
      ) : !clients?.length ? (
        <p className="font-sans text-xs text-text-muted">
          Sin clientes registrados todavía.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {clients.map((cl) => (
            <ClientRow key={cl.slug} client={cl} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ClientRow({ client }: { client: AdminClient }) {
  const [editingKey, setEditingKey] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const setKey = useSetClientAnthropicKey();

  function save(value: string) {
    setKey.mutate(
      { slug: client.slug, apiKey: value },
      {
        onSuccess: () => {
          setEditingKey(false);
          setKeyInput("");
        },
      },
    );
  }

  return (
    <li className="rounded-lg bg-surface-2 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className="font-sans text-sm font-medium text-text-primary truncate">
            {client.name}
          </span>
          <span className="font-mono text-2xs text-text-muted">{client.slug}</span>
          <span className="font-sans text-2xs px-2 py-0.5 rounded-full bg-surface-3 text-text-secondary">
            {client.type}
          </span>
          {!client.active && (
            <span className="font-sans text-2xs px-2 py-0.5 rounded-full bg-warning-bg text-warning-text border border-warning-border">
              inactivo
            </span>
          )}
        </div>
        <span className="font-mono text-2xs text-text-muted shrink-0">
          {client.country ?? "—"}
        </span>
      </div>

      {/* Anthropic key */}
      <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-subtle">
        <div className="flex items-center gap-2 min-w-0">
          <KeyRound size={11} strokeWidth={1.75} className="text-text-muted shrink-0" />
          <span className="font-sans text-2xs text-text-muted">Anthropic:</span>
          {client.anthropicKeySet ? (
            <span className="font-mono text-2xs text-success-text">
              sk-ant-…{client.anthropicKeySuffix}
            </span>
          ) : (
            <span className="font-mono text-2xs text-text-muted italic">
              usa global
            </span>
          )}
        </div>
        {!editingKey ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setEditingKey(true)}
              className="h-6 px-2 text-2xs font-sans rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-3 transition-colors"
            >
              {client.anthropicKeySet ? "Cambiar" : "Setear key"}
            </button>
            {client.anthropicKeySet && (
              <button
                type="button"
                onClick={() => save("")}
                disabled={setKey.isPending}
                title="Volver a usar la key global"
                className="h-6 px-2 text-2xs font-sans rounded-md text-text-muted hover:text-error-text hover:bg-error-bg transition-colors disabled:opacity-50"
              >
                Limpiar
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 flex-1 max-w-md">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="sk-ant-api03-..."
              autoFocus
              className="flex-1 h-7 px-2 text-2xs font-mono rounded-md bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
            />
            <button
              type="button"
              onClick={() => save(keyInput)}
              disabled={!keyInput.trim() || setKey.isPending}
              className="h-7 px-2 text-2xs font-sans font-medium rounded-md bg-magenta text-white hover:bg-magenta-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingKey(false);
                setKeyInput("");
              }}
              className="h-7 px-2 text-2xs font-sans text-text-muted hover:text-text-primary transition-colors"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

function CreateClientForm({ onClose }: { onClose: () => void }) {
  const create = useCreateClient();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [type, setType] = useState<ClientType>("consumer");

  function handleSubmit() {
    if (!slug.trim() || !name.trim()) return;
    create.mutate(
      {
        slug: slug.trim(),
        name: name.trim(),
        country: country.trim() || undefined,
        type,
      },
      {
        onSuccess: () => {
          setSlug("");
          setName("");
          setCountry("");
          onClose();
        },
      },
    );
  }

  return (
    <div className="rounded-lg border border-default bg-surface-2 p-3 mb-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          placeholder="slug (ej: huggies-py)"
          className="h-8 px-2 text-xs font-mono rounded-md bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre comercial"
          className="h-8 px-2 text-xs font-sans rounded-md bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
        />
        <input
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="País (PY, UY, AR…)"
          className="h-8 px-2 text-xs font-sans rounded-md bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
        />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as ClientType)}
          className="h-8 px-2 text-xs font-sans rounded-md bg-surface-1 border border-default text-text-primary focus:border-magenta focus:outline-none"
        >
          {CLIENT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!slug.trim() || !name.trim() || create.isPending}
          className="h-8 px-3 rounded-lg bg-magenta text-white font-sans text-xs font-medium hover:bg-magenta-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {create.isPending ? "Creando…" : "Crear cliente"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="h-8 px-3 rounded-lg text-xs font-sans text-text-muted hover:text-text-primary transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function AccountsSection() {
  const { data: clients } = useAdminClients();
  const create = useCreateCmAccount();
  const [showForm, setShowForm] = useState(false);
  const [clientSlug, setClientSlug] = useState<ClientSlug | "">("");
  const [platform, setPlatform] = useState<Platform>("instagram");
  const [zernioProfileId, setZernioProfileId] = useState("");
  const [zernioAccountId, setZernioAccountId] = useState("");
  const [handle, setHandle] = useState("");

  function handleSubmit() {
    if (!clientSlug || !zernioProfileId || !zernioAccountId) return;
    create.mutate(
      {
        clientSlug: clientSlug as ClientSlug,
        platform,
        zernioProfileId: zernioProfileId.trim(),
        zernioAccountId: zernioAccountId.trim(),
        handle: handle.trim() || undefined,
      },
      {
        onSuccess: () => {
          setZernioProfileId("");
          setZernioAccountId("");
          setHandle("");
          setShowForm(false);
        },
      },
    );
  }

  return (
    <section aria-labelledby="accounts-heading" className="card-surface p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 id="accounts-heading" className="font-sans text-md font-semibold text-text-primary">
          Conectar cuenta nueva
        </h2>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary font-sans text-xs font-medium transition-colors"
          >
            <Plus size={12} strokeWidth={2} />
            Agregar cuenta
          </button>
        )}
      </div>
      <p className="font-sans text-xs text-text-muted mb-3">
        Vincula una cuenta Instagram/Facebook/WhatsApp del cliente con su perfil Zernio.
        Al crearla queda en modo prueba con moderación apagada — el operador la
        activa desde la sección Cuentas del workspace cuando todo está listo.
      </p>

      {showForm && (
        <div className="rounded-lg border border-default bg-surface-2 p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select
              value={clientSlug}
              onChange={(e) => setClientSlug(e.target.value as ClientSlug)}
              className="h-8 px-2 text-xs font-sans rounded-md bg-surface-1 border border-default text-text-primary focus:border-magenta focus:outline-none"
            >
              <option value="">Cliente…</option>
              {clients?.filter((c) => c.active).map((cl) => (
                <option key={cl.slug} value={cl.slug}>
                  {cl.name}
                </option>
              ))}
            </select>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Platform)}
              className="h-8 px-2 text-xs font-sans rounded-md bg-surface-1 border border-default text-text-primary focus:border-magenta focus:outline-none"
            >
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          <input
            type="text"
            value={zernioProfileId}
            onChange={(e) => setZernioProfileId(e.target.value)}
            placeholder="Zernio profile ID"
            className="w-full h-8 px-2 text-xs font-mono rounded-md bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
          />
          <input
            type="text"
            value={zernioAccountId}
            onChange={(e) => setZernioAccountId(e.target.value)}
            placeholder="Zernio account ID"
            className="w-full h-8 px-2 text-xs font-mono rounded-md bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
          />
          <input
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="Handle (ej: huggies_py)"
            className="w-full h-8 px-2 text-xs font-sans rounded-md bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                !clientSlug ||
                !zernioProfileId.trim() ||
                !zernioAccountId.trim() ||
                create.isPending
              }
              className="h-8 px-3 rounded-lg bg-magenta text-white font-sans text-xs font-medium hover:bg-magenta-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {create.isPending ? "Conectando…" : "Conectar cuenta"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="h-8 px-3 rounded-lg text-xs font-sans text-text-muted hover:text-text-primary transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
